import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { trpc } from "@/lib/trpc";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

async function fileToUploadPayload(file: File) {
  const buffer = await file.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i]!);
  return {
    filename: file.name,
    contentType: file.type || "application/pdf",
    base64: btoa(binary),
  };
}

type CreateProjectDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerId?: number;
  proposalId?: number;
  onCreated: (projectId: number) => void;
};

export default function CreateProjectDialog({
  open,
  onOpenChange,
  customerId: initialCustomerId,
  proposalId: initialProposalId,
  onCreated,
}: CreateProjectDialogProps) {
  const utils = trpc.useUtils();
  const [customerId, setCustomerId] = useState<number | null>(initialCustomerId ?? null);
  const [proposalId, setProposalId] = useState<number | null>(initialProposalId ?? null);
  const [projectName, setProjectName] = useState("");
  const [pdfFile, setPdfFile] = useState<File | null>(null);

  const customersQ = trpc.customers.list.useQuery({ limit: 500 }, { enabled: open && !initialCustomerId });
  const proposalsQ = trpc.installProjects.listProposalsForCustomer.useQuery(
    { customerId: customerId! },
    { enabled: open && customerId != null },
  );

  const create = trpc.installProjects.create.useMutation({
    onSuccess: async (res) => {
      toast.success(`Project ${res.projectRef} created`);
      await utils.installProjects.list.invalidate();
      onCreated(res.projectId);
      onOpenChange(false);
    },
    onError: (e) => toast.error(e.message),
  });

  useEffect(() => {
    if (open) {
      setCustomerId(initialCustomerId ?? null);
      setProposalId(initialProposalId ?? null);
      setProjectName("");
      setPdfFile(null);
    }
  }, [open, initialCustomerId, initialProposalId]);

  const handleSubmit = async () => {
    if (!customerId || !proposalId || !projectName.trim()) {
      toast.error("Customer, proposal, and project name are required.");
      return;
    }
    const initialDrawing = pdfFile ? await fileToUploadPayload(pdfFile) : undefined;
    create.mutate({
      customerId,
      proposalId,
      projectName: projectName.trim(),
      initialDrawing,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add Project</DialogTitle>
          <DialogDescription>
            A project is locked to a proposal revision. BOM is snapshotted at creation.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {!initialCustomerId ? (
            <div className="space-y-2">
              <Label>Customer</Label>
              <Select
                value={customerId ? String(customerId) : ""}
                onValueChange={(v) => {
                  setCustomerId(Number(v));
                  setProposalId(null);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select customer" />
                </SelectTrigger>
                <SelectContent>
                  {(Array.isArray(customersQ.data) ? customersQ.data : []).map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}

          <div className="space-y-2">
            <Label>Proposal</Label>
            <Select
              value={proposalId ? String(proposalId) : ""}
              onValueChange={(v) => setProposalId(Number(v))}
              disabled={!customerId}
            >
              <SelectTrigger>
                <SelectValue placeholder={customerId ? "Select proposal" : "Select customer first"} />
              </SelectTrigger>
              <SelectContent>
                {(proposalsQ.data ?? []).map((p) => (
                  <SelectItem key={p.id} value={String(p.id)}>
                    {p.proposalRef} — {p.proposalName ?? "Proposal"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Project Name</Label>
            <Input value={projectName} onChange={(e) => setProjectName(e.target.value)} />
          </div>

          <div className="space-y-2">
            <Label>Upload PDF drawing (optional)</Label>
            <Input
              type="file"
              accept="application/pdf,.pdf"
              onChange={(e) => setPdfFile(e.target.files?.[0] ?? null)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" onClick={() => void handleSubmit()} disabled={create.isPending}>
            {create.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Create Project
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
